import React, { useEffect, useState } from 'react';
import { History, Loader2, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const fmt = (dt) => { if (!dt) return ''; try { return new Date(dt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }); } catch { return dt; } };

// Inline timeline of recorded changes for one object; shown inside edit screens (admins only).
export default function ObjectHistory({ type, id, defaultOpen = false, refreshKey }) {
  const { adminHistory } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id || !open) return;
    let alive = true;
    setRows(null); setError(false);
    adminHistory({ obj_type: type, obj_id: id, limit: 100 })
      .then((d) => { if (alive) setRows(d.rows || []); })
      .catch(() => { if (alive) { setRows([]); setError(true); } });
    return () => { alive = false; };
  }, [adminHistory, type, id, open, refreshKey]);

  if (!id) return null;
  return (
    <div className="mt-3 rounded border" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid={`object-history-${type}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-title" style={{ color: 'var(--navy)' }} data-testid="object-history-toggle">
        <span className="inline-flex items-center gap-1.5"><History size={13} /> History{rows ? ` (${rows.length})` : ''}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3" data-testid="object-history-body">
          {rows === null ? <p className="text-xs flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={12} className="animate-spin" /> Loading…</p>
            : error ? <p className="text-xs" style={{ color: '#8a1f11' }}>Could not load history.</p>
            : rows.length === 0 ? <p className="text-xs" style={{ color: 'var(--olive)' }} data-testid="object-history-empty">No changes recorded yet.</p>
            : (
              <div className="max-h-56 overflow-y-auto pl-1 pr-1">
              <ol className="relative border-l ml-1.5 space-y-2.5" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
                {rows.map((r) => (
                  <li key={r.id} className="ml-3 text-xs" data-testid={`object-history-item-${r.id}`}>
                    <span className="absolute -left-[4.5px] mt-1 h-2 w-2 rounded-full" style={{ background: r.meta?.backfilled ? 'var(--olive)' : 'var(--maroon)' }} />
                    <div style={{ color: 'var(--ink)' }}>{r.event}{(r.before || r.after) && <span style={{ color: 'var(--olive)' }}> · {r.before ?? '—'} → {r.after ?? '—'}</span>}</div>
                    <div style={{ color: 'var(--olive)' }}>{fmt(r.at)} · {r.actor_name || r.actor_email || 'system'}{r.meta?.backfilled ? ' · imported' : ''}</div>
                  </li>
                ))}
              </ol>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
